<template>
  <v-main>
    <v-container fluid>
      <v-flex xs12>
        <v-card class="pa-5">
          <h1 class="text-h5 mb-4">{{ t('terms.title') }}</h1>
          <p>
            {{ t('terms.description') }}
          </p>

          <v-alert v-if="error" type="error" closable>{{ error }}</v-alert>
          <v-alert v-if="success" type="success" closable>{{
            success
          }}</v-alert>

          <v-text-field
            :label="$t('terms.version')"
            v-model="displayVersion"
            readonly
            variant="outlined"
            class="mb-4"
          >
          </v-text-field>

          <div class="terms-container">
            <v-sheet class="pa-3" border>
              <p v-html="termsContent"></p>
            </v-sheet>
          </div>

          <v-checkbox
            v-model="accepted"
            :disabled="!termsContent"
            :label="t('terms.checkbox')"
          >
          </v-checkbox>

          <v-btn
            color="primary"
            :disabled="!accepted || isProcessing"
            @click="acceptTerms"
          >
            {{ isProcessing ? t('terms.processing') : t('terms.button') }}
          </v-btn>
        </v-card>
      </v-flex>
    </v-container>
  </v-main>
</template>

<script>
  import db from '@/firebase/init'
  import { useI18n } from 'vue-i18n'

  export default {
    setup() {
      const { t } = useI18n({ useScope: 'global' })
      return { t }
    },
    data() {
      return {
        termsContent: '',
        latestVersion: null,
        acceptedVersion: null,
        displayVersion: null, // The version user is viewing
        accepted: false,
        error: null,
        success: null,
        isProcessing: false,
        userDocId: null, // Store user's document ID for updating
      }
    },

    methods: {
      /**
       * Fetch the latest terms version from `config/terms`.
       * Then fetch the corresponding terms content from `terms_versions/{latestVersion}`.
       */
      async fetchLatestTerms() {
        try {
          console.log('[TermsAcceptance] Fetching latest terms...')
          const configDoc = await db.collection('config').doc('terms').get()
          if (configDoc.exists) {
            this.latestVersion = configDoc.data().latestVersion
            console.log('[TermsAcceptance] Latest version:', this.latestVersion)

            // Get terms content for the latest version
            const termsDoc = await db
              .collection('terms_versions')
              .doc(this.latestVersion.toString())
              .get()
            if (termsDoc.exists) {
              const termsData = termsDoc.data()
              console.log(
                '[TermsAcceptance] Terms data loaded:',
                Object.keys(termsData)
              )

              // Get language from URL locale or user's preferred language
              const urlLocale = this.$route.params?.locale
              const currentUser = this.$store.getters['UserData/getUser']
              const userLanguage =
                urlLocale || currentUser?.preferredLanguage || 'en'

              console.log(
                '[TermsAcceptance] URL locale:',
                urlLocale,
                'User language:',
                userLanguage
              )

              // Try to get translated content from variants
              const translatedContent =
                termsData.variants?.default?.[userLanguage]

              console.log(
                '[TermsAcceptance] Translated content found:',
                !!translatedContent
              )

              // Fallback to original content if no translation found
              this.termsContent = translatedContent || termsData.content || ''
              this.displayVersion = this.latestVersion // Show latest version
              console.log(
                '[TermsAcceptance] Terms content length:',
                this.termsContent.length
              )
            } else {
              console.error(
                '[TermsAcceptance] Terms document not found for version:',
                this.latestVersion
              )
              this.error = 'Latest terms version not found.'
            }
          } else {
            console.error('[TermsAcceptance] Terms config document not found')
            this.error = 'No terms version available.'
          }
        } catch (err) {
          console.error('[TermsAcceptance] Error fetching terms:', err)
          this.error = 'Failed to load terms. Please try again.'
        }
      },

      /**
       * Fetch the user's accepted terms version.
       */
      async checkUserAcceptance() {
        const currentUser = this.$store.getters['UserData/getUser']
        if (!currentUser || !currentUser.uid) {
          this.error = 'User data is not available. Please log in again.'
          return
        }

        try {
          const querySnapshot = await db
            .collection('users')
            .where('uid', '==', currentUser.uid)
            .get()

          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0]
            this.userDocId = userDoc.id
            this.acceptedVersion = userDoc.data().termsAcceptedVersion || 0

            // If the user has already accepted a version, show the one they accepted
            if (this.acceptedVersion >= this.latestVersion) {
              this.$router.push({ name: 'Dashboard' })
            } else {
              this.displayVersion = this.latestVersion // Show latest version for acceptance
            }
          } else {
            console.error(
              'User document does NOT exist in Firestore! UID:',
              currentUser.uid
            )
            this.error = `User record not found. Check Firestore manually for UID: ${currentUser.uid}`
          }
        } catch (err) {
          console.error('Error checking user acceptance:', err)
          this.error = 'Failed to verify acceptance status.'
        }
      },

      /**
       * Accept the latest terms and update Firestore.
       */
      async acceptTerms() {
        if (!this.userDocId) {
          this.error = 'User record not found. Cannot save acceptance.'
          return
        }

        this.isProcessing = true
        try {
          await db.collection('users').doc(this.userDocId).update({
            termsAcceptedVersion: this.latestVersion,
          })

          // Update Vuex state immediately
          this.$store.commit('UserData/updateUser', {
            ...this.$store.getters['UserData/getUser'], // Preserve existing user data
            termsAcceptedVersion: this.latestVersion,
          })

          this.success = 'Terms accepted successfully!'
          setTimeout(() => this.$router.push({ name: 'Dashboard' }), 2000)
        } catch (err) {
          console.error('Error saving acceptance:', err)
          this.error = 'Failed to save acceptance. Please try again.'
        } finally {
          this.isProcessing = false
        }
      },
    },

    async created() {
      console.log('[TermsAcceptance] Component created, starting data fetch...')
      try {
        await this.fetchLatestTerms()
        await this.checkUserAcceptance()
        console.log('[TermsAcceptance] Data fetch completed successfully')
      } catch (error) {
        console.error('[TermsAcceptance] Error in created():', error)
        this.error = 'Failed to load terms data'
      }
    },

    watch: {
      // Watch for route locale changes and re-fetch terms content
      '$route.params.locale': {
        handler: async function (newLocale, oldLocale) {
          if (newLocale !== oldLocale) {
            console.log(
              '[TermsAcceptance] Locale changed from',
              oldLocale,
              'to',
              newLocale
            )
            try {
              await this.fetchLatestTerms()
            } catch (error) {
              console.error(
                '[TermsAcceptance] Error re-fetching terms after locale change:',
                error
              )
              this.error = 'Failed to load terms for new language'
            }
          }
        },
        immediate: false,
      },
    },
  }
</script>

<style>
  .terms-container {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #ccc;
    padding: 10px;
    margin-bottom: 10px;
  }
</style>
